import React, { FC, ReactNode } from 'react';
import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { getAuctionName } from '@app/helpers/nft/getAuctionName';
import { format } from 'date-fns';
import { toOptionalFixed } from '@app/helpers/format';
import { formatUSD } from '@app/helpers/formatUSD';

interface Props {
  auctionType: string;
  auctionDescription?: string;
  balance: string;
  currentPrice?: string;
  startPrice?: string;
  reservePrice?: string;
  bestOffer?: string | undefined;
  discount?: number;
  duration?: [number, number];
  revealPeriod?: string;

  deposit?: string;
  depositUsd?: number | null;
}

export const DetailsSection: FC<Props> = ({
  auctionType,
  auctionDescription,
  bestOffer,
  reservePrice,
  discount,
  balance,
  duration,
  startPrice,
  currentPrice,
  revealPeriod,
  deposit,
  depositUsd
}) => {
  return (
    <VStack alignItems="flex-start" gap={3}>
      <InfoRow
        label="Auction type"
        value={
          <Text fontSize={14} color="orange">
            {getAuctionName(auctionType)}
          </Text>
        }
      />
      {auctionDescription && (
        <Box mb={3}>
          <Text color="neutral.100" fontSize={14} mt={2} lineHeight={1.1}>
            {auctionDescription}
          </Text>
        </Box>
      )}
      {deposit && (
        <InfoRow
          label="Deposit"
          value={
            <HStack gap={2}>
              {depositUsd && (
                <Text color="neutral.300">{formatUSD.format(depositUsd)}</Text>
              )}
              <Text color="neutral.100"> {toOptionalFixed(+deposit, 3)}</Text>
            </HStack>
          }
        />
      )}
      {discount && (
        <InfoRow
          label="Discount"
          value={
            <Flex alignItems="center">
              {discount < 0 && (
                <HStack w="100%">
                  <Text fontSize="14px" fontWeight="400" color="success">
                    {Math.abs(discount).toFixed(2)}%
                  </Text>
                </HStack>
              )}
              {discount > 0 && (
                <HStack w="100%">
                  <Text fontSize="14px" fontWeight="400" color="error">
                    {discount.toFixed(2)}%
                  </Text>
                </HStack>
              )}
              {discount === 0 && (
                <HStack w="100%">
                  <Text fontSize="14px" fontWeight="400" color="neutral.400">
                    -
                  </Text>
                </HStack>
              )}
            </Flex>
          }
        />
      )}
      {startPrice && <InfoRow label="Start Price" value={startPrice} />}
      {currentPrice && <InfoRow label="Current Price" value={currentPrice} />}
      {reservePrice && <InfoRow label="Reserve Price" value={reservePrice} />}
      {bestOffer && <InfoRow label="Best Offer" value={bestOffer} />}
      {duration && (
        <InfoRow
          label="Starts at"
          value={format(new Date(duration[0] * 1000), 'dd MMM yyyy HH:mm')}
        />
      )}
      {duration && (
        <InfoRow
          label="Ends at"
          value={format(new Date(duration[1] * 1000), 'dd MMM yyyy HH:mm')}
        />
      )}
      {revealPeriod && <InfoRow label="Reveal period" value={revealPeriod} />}
      {/*<InfoRow label="Discount/Premium" value={discount} />*/}
    </VStack>
  );
};

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => {
  return (
    <HStack alignItems="center" justifyContent="space-between" width="100%">
      <Text fontSize={14} color="gray.15">
        {label}
      </Text>
      {typeof value === 'string' ? (
        <Text fontSize={14} color="white">
          {value}
        </Text>
      ) : (
        value
      )}
    </HStack>
  );
};
